import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { logger } from '@assess/shared/logger';
import { IValidationResult } from '@assess/shared/yup-extensions';

type YupFn<T = any> = (value: T, ...args: any[]) => Promise<IValidationResult<T>>;
type AsyncValidationErrors = Observable<ValidationErrors | null>;
const createError = (message: string, details?: any) => {
  return { schema: { details, message } };
};
const jsonFactory = (fn: (value: any, ...args: any[]) => AsyncValidationErrors) => {
  return (value: any): AsyncValidationErrors => {
    try {
      value = JSON.parse(value);

      return fn(value);
    } catch {
      return of(createError('Invalid JSON'));
    }
  };
};
const yupFactory = (fn: YupFn) => {
  return (value: any): AsyncValidationErrors => {
    return from(fn(value)).pipe(
      map((v: any) => {
        if (v.valid) {
          return null;
        }

        logger.error({ context: 'asyncSchemaValidator', data: { errors: v.result } }, 'Invalid schema');

        return createError('Invalid schema', v.result);
      })
    );
  };
};
/**
 * An async validator for Angular's reactive forms that will validate a control's string value against a Yup
 * schema via the common wrapper interface from `shared-yup-extensions`.
 */
export const asyncYupValidator = (fn: YupFn, json = true): AsyncValidatorFn => {
  return (control: AbstractControl) => {
    const yupFn = yupFactory(fn);
    const value = control?.value;

    return json ? jsonFactory(yupFn)(value) : yupFn(value);
  };
};
